<template>
	<div
		class="page-background"
		id="home"
	>
		<div class="page-content">
			<OnboardingHero
				v-if="shouldShowOnboardingHero"
				:steps="onboardingSteps"
				@step-click="onOnboardingStepClick"
			/>

			<AlertsBanner
				v-if="alerts.length"
				:alerts="alerts"
				@dismiss="dismissAlert"
				@clear-all="clearAllAlerts"
			/>

			<Greeting
				:first-name="firstName"
				:running="runningTasks"
				:last-task="lastTask"
				:has-api-key="hasApiKey"
				:tasks-run="tasksRun"
				:refreshing="refreshing"
				:last-refreshed-label="lastRefreshedLabel"
				:now="now"
				@refresh="refresh"
			/>

			<QuickActions
				:user-state="quickActionState"
				:ready="userDataReady"
			/>

			<div
				class="home__bento"
				data-test="home-bento"
			>
				<ComputeActivityTile :ready="taskMetadataHistoryReady" />
				<ActiveTasksTile
					:tasks-run="tasksRun"
					:now="now"
				/>
				<ApiKeyTile
					ref="apiKeyTile"
					@keys-loaded="onKeysLoaded"
				/>
				<QuotasTile />
				<CoresTile :custom-cores="customCores" />
				<LearnTile
					:user-state="learnTileState"
					:ready="userDataReady"
				/>
			</div>

			<RecentlyCompletedTable />

			<section class="home__examples">
				<header class="home__examples-head">
					<h3 class="home__examples-title sgnl-paragraph-l-medium sgnl-text-color-default">
						Example Repositories
					</h3>
					<v-btn
						variant="text"
						size="small"
						to="/apps"
						class="bento-tile__action"
					>
						Browse all
						<ArrowRight16 />
					</v-btn>
				</header>
				<Transition
					name="examples-swap"
					mode="out-in"
				>
					<div
						:key="applicationsList.length > 0 ? 'real' : 'skeleton'"
						class="home__examples-grid"
					>
						<template v-if="applicationsList.length > 0">
							<ApplicationSlide
								v-for="(application, i) in applicationsList.slice(0, 4)"
								:key="i"
								:slide-info="application"
							/>
						</template>
						<template v-else>
							<v-card
								v-for="i in [1, 2, 3, 4]"
								:key="i"
								class="home__example-skeleton"
								variant="flat"
							>
								<SkeletonBlock
									height="calc(var(--sgnl-font-line-height-paragraph-s) * 2)"
									radius="var(--sgnl-border-radius-6)"
								/>
								<div class="home__example-skeleton-tags">
									<SkeletonBlock
										width="100px"
										height="var(--sgnl-unit-24)"
										radius="var(--sgnl-border-radius-8)"
									/>
									<SkeletonBlock
										width="100px"
										height="var(--sgnl-unit-24)"
										radius="var(--sgnl-border-radius-8)"
									/>
								</div>
								<SkeletonBlock btn />
							</v-card>
						</template>
					</div>
				</Transition>
			</section>
		</div>
	</div>
</template>

<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent } from "vue";
import { mapState } from "pinia";
import type { RouteLocationRaw } from "vue-router";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";

import OnboardingHero, { type OnboardingStep } from "@/components/Home/OnboardingHero.vue";
import AlertsBanner, { type HomeAlert } from "@/components/Home/AlertsBanner.vue";
import Greeting from "@/components/Home/Greeting.vue";
import QuickActions, { type QuickActionUserState } from "@/components/Home/QuickActions.vue";
import ComputeActivityTile from "@/components/Home/ComputeActivityTile.vue";
import ActiveTasksTile from "@/components/Home/ActiveTasksTile.vue";
import ApiKeyTile, { type KeyAlertInfo, type KeysLoadedPayload } from "@/components/Home/ApiKeyTile.vue";
import QuotasTile from "@/components/Home/QuotasTile.vue";
import CoresTile from "@/components/Home/CoresTile.vue";
import LearnTile, { type LearnTileUserState } from "@/components/Home/LearnTile.vue";
import RecentlyCompletedTable from "@/components/Home/RecentlyCompletedTable.vue";
import ApplicationSlide from "@/components/Home/ApplicationSlide.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";

// Shared (non-scoped) selectors used by every tile (.bento-tile, .bento-tile__header,
// .bento-tile__action, …). Imported here so the styles only load with the Home route.
import "@/components/Home/bento.css";

import { useUserStore } from "@/stores/user";
import { useCoresStore } from "@/stores/cores";
import { useDataSourcesStore } from "@/stores/dataSources";
import { useRepositoriesStore } from "@/stores/repositories";
import { useTasksStore } from "@/stores/tasks";
import { useBuildsStore } from "@/stores/builds";
import { useRootStore } from "@/stores/root";

import type { Build } from "@/types/api/builds";
import { BuildStatusE } from "@/types/api/builds";
import type { Task } from "@/types/api/tasks";
import { TaskStatusE } from "@/types/api/tasks";
import type { Core } from "@/types/api/cores";
import type { OnboardingState } from "@/types/user";
import {
	convertToMilliseconds,
	coreNameFromSpecs,
	HOME_ALERT_DISMISSALS_PREFIX,
	repoFullNameFromUrl,
	shortenID,
} from "@/js/utilities";
import moment from "moment";

type StepId = OnboardingStep["id"];
type StepDef = {
	id: StepId;
	label: string;
	flag: keyof OnboardingState;
	route: RouteLocationRaw;
};

const STEPS: StepDef[] = [
	{ id: "firstTask", label: "Run your first task", flag: "Onboarding_PreviewTask_Completed", route: "/launch" },
	{ id: "customCore", label: "Configure a custom core", flag: "Onboarding_CustomCore_Completed", route: "/cores" },
	{
		id: "repository",
		label: "Connect a repository",
		flag: "Onboarding_Repository_Completed",
		route: "/repositories",
	},
	{
		id: "apiKey",
		label: "Create API key",
		flag: "Onboarding_APIKey_Completed",
		route: { name: "Settings_cloudEngineAPI" },
	},
];

// Cache the onboarded state so returning users skip the hero on first paint
// instead of waiting for /user-data to resolve. Avoids a CLS shift when the
// hero would otherwise render then disappear.
const ONBOARDED_CACHE_KEY = "home.onboarded";
const readCachedOnboarded = (): boolean => {
	try {
		return localStorage.getItem(ONBOARDED_CACHE_KEY) === "true";
	} catch {
		return false;
	}
};
const writeCachedOnboarded = (done: boolean): void => {
	try {
		localStorage.setItem(ONBOARDED_CACHE_KEY, String(done));
	} catch {
		/* storage unavailable */
	}
};

type ComponentData = {
	apiKeyCount: number;
	apiKeyExpired: KeyAlertInfo[];
	apiKeyExpiring: KeyAlertInfo[];
	apiKeyFetchPending: boolean;
	alertDismissals: Record<string, boolean>;
	refreshing: boolean;
	lastRefreshedAt: number | null;
	now: number;
	tickTimer: undefined | ReturnType<typeof setInterval>;
	cachedOnboardingDone: boolean;
	taskMetadataHistoryReady: boolean;
};

export default defineComponent({
	name: "Home",
	components: {
		OnboardingHero,
		AlertsBanner,
		Greeting,
		QuickActions,
		ComputeActivityTile,
		ActiveTasksTile,
		ApiKeyTile,
		QuotasTile,
		CoresTile,
		LearnTile,
		RecentlyCompletedTable,
		ApplicationSlide,
		SkeletonBlock,
		ArrowRight16,
	},
	setup() {
		const userStore = useUserStore();
		const coresStore = useCoresStore();
		const dataSourcesStore = useDataSourcesStore();
		const repositoriesStore = useRepositoriesStore();
		const tasksStore = useTasksStore();
		const buildsStore = useBuildsStore();
		const rootStore = useRootStore();
		return { userStore, coresStore, dataSourcesStore, repositoriesStore, tasksStore, buildsStore, rootStore };
	},
	data: (): ComponentData => ({
		apiKeyCount: 0,
		apiKeyExpired: [],
		apiKeyExpiring: [],
		apiKeyFetchPending: true,
		alertDismissals: {},
		refreshing: false,
		lastRefreshedAt: null,
		now: Date.now(),
		tickTimer: undefined,
		cachedOnboardingDone: readCachedOnboarded(),
		taskMetadataHistoryReady: false,
	}),
	computed: {
		...mapState(useRootStore, { applicationsList: "applicationsList" }),
		...mapState(useUserStore, {
			currentUserDB: "currentUserObjectFromDB",
			authUser: "currentAuthenticatedUserObject",
			currentUserTierCode: "currentUserTierCode",
			monthlyQuotaUsage: "monthlyQuotaUsage",
		}),
		...mapState(useCoresStore, {
			localCores: "localCoresList",
			remoteCores: "remoteCoresList",
			defaultCores: "allDefaultCores",
		}),
		...mapState(useDataSourcesStore, {
			driveList: "driveList",
			gatewayList: "gatewayList",
			bucketList: "bucketList",
		}),
		...mapState(useRepositoriesStore, { connectedRepositories: "connectedRepositories" }),
		...mapState(useTasksStore, {
			taskHistory: "taskHistory",
			activeTasks: "activeTasks",
			taskMetadataHistory: "taskMetadataHistory",
		}),
		...mapState(useBuildsStore, { buildHistory: "buildHistory", activeBuilds: "activeBuilds" }),

		userDataReady(): boolean {
			return (
				this.currentUserDB !== undefined && this.connectedRepositories !== undefined && !this.apiKeyFetchPending
			);
		},
		alertsHistoryReady(): boolean {
			return this.userDataReady && this.buildHistory !== undefined && this.taskHistory !== undefined;
		},
		lastRefreshedLabel(): string {
			if (!this.lastRefreshedAt) return "";
			// Clamp the reference to lastRefreshedAt so the 1s ticker lag
			// can't briefly produce future-tense output ("in a few seconds").
			const ref = Math.max(this.now, this.lastRefreshedAt);
			return `Last refreshed ${moment(this.lastRefreshedAt).from(ref)}`;
		},
		onboardingState(): Partial<OnboardingState> {
			return this.currentUserDB?.OnboardingState ?? {};
		},
		hasApiKey(): boolean {
			return this.apiKeyCount > 0 || !!this.onboardingState.Onboarding_APIKey_Completed;
		},
		customCores(): number {
			return ((this.remoteCores as Core[] | undefined) ?? []).filter(
				(c) => !c.Disabled && !c.Deprecated && !c.Hidden,
			).length;
		},
		dataSourceCount(): number {
			return (this.driveList?.length ?? 0) + (this.gatewayList?.length ?? 0) + (this.bucketList?.length ?? 0);
		},
		repoCount(): number {
			return (this.connectedRepositories ?? []).length;
		},
		// Count of tasks currently loaded into the paged history, not a lifetime
		// figure. Use as a boolean "has run anything" signal rather than a threshold.
		tasksRun(): number {
			return (this.taskHistory ?? []).length;
		},
		onboardingSteps(): OnboardingStep[] {
			const stateFlags = this.onboardingState;
			const firstTaskRun = !!stateFlags.Onboarding_FirstTask_Completed;
			const derived: Record<StepId, boolean> = {
				apiKey: this.hasApiKey,
				customCore: this.customCores > 0,
				firstTask: false,
				repository: this.repoCount > 0,
			};
			const mostRecentTaskId = ((this.taskHistory as Task[] | undefined) ?? [])[0]?.TaskID;
			return STEPS.map((s) => {
				if (s.id === "firstTask") {
					const route = firstTaskRun
						? mostRecentTaskId
							? { name: "TaskDetail", params: { id: mostRecentTaskId } }
							: "/tasks"
						: s.route;
					return {
						id: s.id,
						label: firstTaskRun ? "View first task details" : s.label,
						done: !!stateFlags[s.flag],
						to: this.routeToString(route),
					};
				}
				return {
					id: s.id,
					label: s.label,
					done: !!stateFlags[s.flag] || derived[s.id],
					to: this.routeToString(s.route),
				};
			});
		},
		allOnboardingDone(): boolean {
			return this.onboardingSteps.every((s) => s.done);
		},
		shouldShowOnboardingHero(): boolean {
			return this.userDataReady ? !this.allOnboardingDone : !this.cachedOnboardingDone;
		},
		firstName(): string | null {
			const attrs = this.authUser?.attributes;
			const name = attrs?.name ?? attrs?.given_name ?? "";
			if (!name) return null;
			return name.split(" ")[0] ?? null;
		},
		runningTasks(): { id: string; core: string; startedMs: number }[] {
			const list = ((this.activeTasks as Task[] | undefined) ?? []).filter(
				(t) => t.Status === TaskStatusE.InProgress,
			);
			return list.map((t) => ({
				id: t.TaskID,
				core: this.coreNameForBuild(t.BuildID),
				startedMs: t.StartedAt ? convertToMilliseconds(t.StartedAt) : 0,
			}));
		},
		lastTask(): { id: string; whenLabel: string; core: string } | null {
			const tasks = (this.taskHistory as Task[] | undefined) ?? [];
			const completed = tasks
				.filter((t) => t.Status === TaskStatusE.Completed && t.FinishedAt)
				.reduce<Task | undefined>((best, t) => {
					if (!best) return t;
					return t.FinishedAt > best.FinishedAt ? t : best;
				}, undefined);
			if (!completed) return null;
			const finishedMs = convertToMilliseconds(completed.FinishedAt);
			const whenLabel = moment(finishedMs).fromNow();
			return {
				id: completed.TaskID,
				whenLabel,
				core: this.coreNameForBuild(completed.BuildID),
			};
		},
		quickActionState(): QuickActionUserState {
			const lastRepo = (this.connectedRepositories ?? [])[0];
			const lastRepoName = lastRepo ? repoFullNameFromUrl(lastRepo.RemoteURL) : null;
			const maxQuotaPct = (this.monthlyQuotaUsage ?? []).reduce((m, q) => Math.max(m, q.pct), 0);
			return {
				hasApiKey: this.hasApiKey,
				onboarded: this.allOnboardingDone,
				customCores: this.customCores,
				dataSources: this.dataSourceCount,
				repos: this.repoCount,
				tasksRun: this.tasksRun,
				lastTask: this.lastTask,
				lastRepoName,
				lastRepoId: lastRepo?.RepositoryID ?? null,
				isEnterprise: !!this.currentUserTierCode?.startsWith("enterprise-tier-"),
				quotaNearLimit: maxQuotaPct >= 0.8,
				quotaAtLimit: maxQuotaPct >= 1,
			};
		},
		learnTileState(): LearnTileUserState {
			const userID = this.authUser?.attributes?.["custom:user_id"] ?? "anon";
			return {
				userId: userID,
				hasApiKey: this.hasApiKey,
				apiKeyCount: this.apiKeyCount,
				tasksRun: this.tasksRun,
				repos: this.repoCount,
				dataSources: this.dataSourceCount,
				customCores: this.customCores,
			};
		},
		// Per-user localStorage key. Null until the auth user is available; the
		// watcher below loads on first non-null transition.
		dismissedStorageKey(): string | null {
			const userID = this.authUser?.attributes?.["custom:user_id"];
			return userID ? `${HOME_ALERT_DISMISSALS_PREFIX}${userID}` : null;
		},
		generatedAlerts(): HomeAlert[] {
			const list: HomeAlert[] = [];

			// 1. Most recent failed build.
			const failedBuild = ((this.buildHistory as Build[] | undefined) ?? []).find(
				(b) => b.Status === BuildStatusE.Error,
			);
			if (failedBuild) {
				const app = failedBuild.Application;
				const repoLabel =
					app.Type === "Repository" ? repoFullNameFromUrl(app.Repository?.RemoteURL) : "Code Playground";
				const whenMs = convertToMilliseconds(failedBuild.FinishedAt || failedBuild.UpdatedAt);
				const parts = [repoLabel, shortenID(failedBuild.BuildID), moment(whenMs).fromNow()].filter((p) => p);
				list.push({
					id: `build-failed-${failedBuild.BuildID}`,
					tone: "amber",
					title: "A recent build failed.",
					body: parts.join(" · "),
					actionLabel: "View build →",
					actionTo: "/tasks",
				});
			}

			// 2. Most recent failed task (Error / Stopped / NotFoundError).
			const failedTask = ((this.taskHistory as Task[] | undefined) ?? []).find(
				(t) =>
					t.Status === TaskStatusE.Error ||
					t.Status === TaskStatusE.Stopped ||
					t.Status === TaskStatusE.NotFoundError,
			);
			if (failedTask) {
				const whenMs = convertToMilliseconds(failedTask.FinishedAt || failedTask.UpdatedAt);
				list.push({
					id: `task-failed-${failedTask.TaskID}`,
					tone: "amber",
					title: "A recent task failed.",
					body: `${shortenID(failedTask.TaskID)} · ${moment(whenMs).fromNow()}`,
					actionLabel: "Open task →",
					actionTo: { name: "TaskDetail", params: { id: failedTask.TaskID } },
				});
			}

			// 3-4. Quota alerts — show "hit" if any quota is at/over 100%, otherwise "near" if any is ≥80%.
			const quotas = this.monthlyQuotaUsage ?? [];
			const hitQuota = quotas.find((q) => q.pct >= 1);
			if (hitQuota) {
				list.push({
					id: `quota-hit-${hitQuota.id}`,
					tone: "red",
					title: `You hit your monthly ${hitQuota.label} limit.`,
					body: "Upgrade to keep running tasks this billing period.",
					actionLabel: "Upgrade →",
					actionTo: "/billing",
				});
			} else {
				const nearQuota = quotas.find((q) => q.pct >= 0.8);
				if (nearQuota) {
					list.push({
						id: `quota-near-${nearQuota.id}`,
						tone: "amber",
						title: `${nearQuota.label} at ${Math.round(nearQuota.pct * 100)}% of monthly limit.`,
						actionLabel: "View usage →",
						actionTo: "/usage",
					});
				}
			}

			// 5. Expired API keys — one alert per key so dismissals are per-key.
			for (const k of this.apiKeyExpired) {
				list.push({
					id: `api-key-expired-${k.keyId}`,
					tone: "red",
					title: `API key "${k.name}" has expired.`,
					actionLabel: "Manage keys →",
					actionTo: { name: "Settings_cloudEngineAPI" },
				});
			}

			// 6. API keys expiring in the next 7 days — one alert per key.
			for (const k of this.apiKeyExpiring) {
				list.push({
					id: `api-key-expiring-${k.keyId}`,
					tone: "amber",
					title: `API key "${k.name}" expires ${moment.unix(k.validUntil).fromNow()}.`,
					actionLabel: "Manage keys →",
					actionTo: { name: "Settings_cloudEngineAPI" },
				});
			}

			return list;
		},
		alerts(): HomeAlert[] {
			return this.generatedAlerts.filter((a) => !this.alertDismissals[a.id]);
		},
	},
	methods: {
		routeToString(to: RouteLocationRaw): string {
			if (typeof to === "string") return to;
			const resolved = this.$router.resolve(to);
			return resolved.fullPath;
		},
		coreNameForBuild(buildId: string): string {
			const build = ((this.buildHistory as Build[] | undefined) ?? []).find((b) => b.BuildID === buildId);
			return coreNameFromSpecs(build?.BuildCoreSpecs, (this.defaultCores as Core[] | undefined) ?? []);
		},
		onKeysLoaded(payload: KeysLoadedPayload) {
			this.apiKeyCount = payload.count;
			this.apiKeyExpired = payload.expired;
			this.apiKeyExpiring = payload.expiring;
			this.apiKeyFetchPending = false;
			if (payload.count > 0) this.markStepsDone(["Onboarding_APIKey_Completed"]);
		},
		// Once a step has ever been completed, the flag stays true even if the user later
		// deletes the resource — onboarding shouldn't re-nag for something they've already done.
		// Batches multiple flags into a single PATCH (initial load often hits 2-3 at once).
		onOnboardingStepClick(step: OnboardingStep) {
			if (
				step.id === "firstTask" &&
				this.onboardingState.Onboarding_FirstTask_Completed &&
				!this.onboardingState.Onboarding_PreviewTask_Completed
			) {
				this.markStepsDone(["Onboarding_PreviewTask_Completed"]);
			}
		},
		markStepsDone(flagKeys: Array<keyof OnboardingState>) {
			const state = this.onboardingState;
			const next: Partial<OnboardingState> = {};
			for (const key of flagKeys) {
				if (!state[key]) next[key] = true;
			}
			if (Object.keys(next).length === 0) return;
			this.userStore.syncOnboardingStateWithRemote(next);
			// Fire only when this call newly flipped a flag AND every step is now done.
			// Reloads for already-onboarded users hit the early return above, so no dupes.
			if (this.allOnboardingDone) {
				this.$posthog?.capture("home_onboarding_all_completed");
			}
		},
		dismissAlert(id: string) {
			const alert = this.generatedAlerts.find((a) => a.id === id);
			this.$posthog?.capture("home_alert_dismissed", { alert_id: id, tone: alert?.tone });
			this.alertDismissals = { ...this.alertDismissals, [id]: true };
			this.persistDismissals();
		},
		clearAllAlerts() {
			this.$posthog?.capture("home_alerts_cleared_all", { count: this.generatedAlerts.length });
			const next: Record<string, boolean> = { ...this.alertDismissals };
			this.generatedAlerts.forEach((a) => {
				next[a.id] = true;
			});
			this.alertDismissals = next;
			this.persistDismissals();
		},
		loadDismissals() {
			const key = this.dismissedStorageKey;
			if (!key) return;
			try {
				const raw = localStorage.getItem(key);
				if (!raw) return;
				const parsed = JSON.parse(raw);
				if (parsed && typeof parsed === "object") {
					this.alertDismissals = parsed as Record<string, boolean>;
				}
			} catch {
				// Corrupt entry or storage unavailable — ignore.
			}
		},
		persistDismissals() {
			const key = this.dismissedStorageKey;
			if (!key) return;
			try {
				localStorage.setItem(key, JSON.stringify(this.alertDismissals));
			} catch {
				// Quota or storage unavailable — ignore.
			}
		},
		// Drop dismissal entries whose alert is no longer surfaced. Safe only
		// once alertsHistoryReady is true — otherwise generatedAlerts is empty
		// for the wrong reason (fetches in flight) and we'd wipe live entries.
		pruneDismissals() {
			if (!this.alertsHistoryReady) return;
			const key = this.dismissedStorageKey;
			if (!key) return;
			const currentIds = new Set(this.generatedAlerts.map((a) => a.id));
			const next: Record<string, boolean> = {};
			let changed = false;
			for (const id of Object.keys(this.alertDismissals)) {
				if (currentIds.has(id)) next[id] = this.alertDismissals[id];
				else changed = true;
			}
			if (!changed) return;
			this.alertDismissals = next;
			this.persistDismissals();
		},
		async refresh() {
			if (this.refreshing) return;
			this.$posthog?.capture("home_refresh_clicked", {
				has_running: this.runningTasks.length > 0,
				has_last_task: !!this.lastTask,
			});
			this.refreshing = true;
			const apiKeyTile = this.$refs.apiKeyTile as { loadKeys: () => Promise<void> } | undefined;
			try {
				await Promise.allSettled([
					this.tasksStore.getTasksMetadataHistoryWithinDays(7),
					this.tasksStore.fetchActiveTasksFromRemote(),
					this.tasksStore.fetchNextTaskBatchFromRemote(),
					this.buildsStore.fetchActiveBuildsFromRemote(),
					this.buildsStore.fetchNextBuildBatchFromRemote(),
					this.coresStore.getCoresFilteredCoresByUserTier(),
					this.repositoriesStore.fetchRemoteRepositories(),
					this.dataSourcesStore.fetchAllDataSources(),
					this.userStore.fetchCurrentUserObjectFromDB(),
					apiKeyTile?.loadKeys() ?? Promise.resolve(),
				]);
			} finally {
				this.refreshing = false;
				this.lastRefreshedAt = Date.now();
			}
		},
	},
	watch: {
		// Gate on userDataReady so onboardingState reflects the server's view before
		// we try to skip a PATCH. Otherwise a stale store count (e.g. after navigating
		// back to Home) would race the user fetch and re-PATCH on every visit.
		customCores(n: number) {
			if (n > 0 && this.userDataReady) this.markStepsDone(["Onboarding_CustomCore_Completed"]);
		},
		dataSourceCount(n: number) {
			if (n > 0 && this.userDataReady) this.markStepsDone(["Onboarding_DataSource_Completed"]);
		},
		repoCount(n: number) {
			if (n > 0 && this.userDataReady) this.markStepsDone(["Onboarding_Repository_Completed"]);
		},
		tasksRun(n: number) {
			if (n > 0 && this.userDataReady) this.markStepsDone(["Onboarding_FirstTask_Completed"]);
		},
		userDataReady(ready: boolean) {
			if (!ready) return;
			// One batched PATCH on first ready transition instead of one per resource.
			const toMark: Array<keyof OnboardingState> = [];
			if (this.customCores > 0) toMark.push("Onboarding_CustomCore_Completed");
			if (this.dataSourceCount > 0) toMark.push("Onboarding_DataSource_Completed");
			if (this.repoCount > 0) toMark.push("Onboarding_Repository_Completed");
			if (this.tasksRun > 0) toMark.push("Onboarding_FirstTask_Completed");
			if (toMark.length > 0) this.markStepsDone(toMark);
			writeCachedOnboarded(this.allOnboardingDone);
		},
		allOnboardingDone(done: boolean) {
			if (this.userDataReady) writeCachedOnboarded(done);
		},
		alertsHistoryReady(ready: boolean) {
			if (ready) this.pruneDismissals();
		},
		now(curr: number, prev: number) {
			if (new Date(prev).toDateString() !== new Date(curr).toDateString()) {
				this.refresh();
			}
		},
		// Load persisted dismissals on first non-null key (i.e. once authUser is set).
		dismissedStorageKey: {
			immediate: true,
			handler(key: string | null, prev: string | null) {
				if (key && key !== prev) {
					this.loadDismissals();
					this.pruneDismissals();
				}
			},
		},
	},
	created() {
		// App.vue bootstraps cores/dataSources/tasks/builds on session "Ready". Home
		// only kicks off what App.vue doesn't: repositories, active builds (commented
		// out in initialiseBuildHistory), and the 7-day task-metadata window.
		if (this.connectedRepositories === undefined) {
			this.repositoriesStore.fetchRemoteRepositories();
		}
		this.buildsStore.fetchActiveBuildsFromRemote();
		this.tasksStore.getTasksMetadataHistoryWithinDays(7).finally(() => {
			this.taskMetadataHistoryReady = true;
		});
		// Single 1s ticker shared with Greeting/ActiveTasksTile via :now prop.
		this.tickTimer = setInterval(() => {
			this.now = Date.now();
		}, 1000);
		this.lastRefreshedAt = Date.now();
	},
	beforeUnmount() {
		if (this.tickTimer) clearInterval(this.tickTimer);
	},
});
</script>

<style lang="scss" scoped>
.home__bento {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: var(--sgnl-space-16);
}

.home__examples {
	background: var(--sgnl-default-primary-background);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-6);
	padding: var(--sgnl-space-16);
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.home__examples-head {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
}

.home__examples-title {
	margin: 0;
}

.home__examples-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: var(--sgnl-space-16);
	min-height: var(--sgnl-unit-192);
}

.examples-swap-enter-active,
.examples-swap-leave-active {
	transition:
		opacity var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-standard),
		filter var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-standard);
}
.examples-swap-enter-from,
.examples-swap-leave-to {
	opacity: 0;
	filter: blur(2px);
}

.home__example-skeleton {
	background: var(--sgnl-default-primary-background);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	display: grid;
	gap: var(--sgnl-space-8);
	grid-template-rows: auto 1fr auto;
	padding: var(--sgnl-space-8);
	min-height: var(--sgnl-unit-192);
}

.home__example-skeleton-tags {
	display: flex;
	flex-wrap: wrap;
	gap: var(--sgnl-space-4);
}

@media (max-width: 1099px) {
	.home__bento {
		grid-template-columns: repeat(2, 1fr);
	}
	.home__bento > :deep(.bento-tile--span-2) {
		grid-column: 1 / -1;
	}
	.home__examples-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (max-width: 719px) {
	.home__bento {
		grid-template-columns: 1fr;
	}
	.home__bento > :deep(.bento-tile--span-2) {
		grid-column: auto;
	}
	.home__examples-grid {
		grid-template-columns: 1fr;
	}
}
</style>
